import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { api, getAccessToken } from '../../api/client';
import type { Business, DetailedReport, SalesSummary, TodayLiveSummary } from '../../api/types';
import { Alert } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, StatCard } from '../../components/ui/Card';
import { Input, Select } from '../../components/ui/Input';
import { ReportsCharts } from '../../components/reports/ReportsCharts';
import { defaultDateRange, formatCurrency, formatDate } from '../../utils/format';

export function ReportsPage() {
  const initialRange = defaultDateRange();
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const [licenseId, setLicenseId] = useState('all');
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [summary, setSummary] = useState<SalesSummary | null>(null);
  const [reports, setReports] = useState<DetailedReport[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const initialFiltersRef = useRef({ startDate, endDate, licenseId });
  const activeFiltersRef = useRef({ startDate, endDate, licenseId });

  useEffect(() => {
    api.get<Business[]>('/api/license/businesses').then(setBusinesses).catch(() => null);
  }, []);

  const loadReports = useCallback(async (nextStart: string, nextEnd: string, nextLicense: string) => {
    activeFiltersRef.current = { startDate: nextStart, endDate: nextEnd, licenseId: nextLicense };
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        startDate: nextStart,
        endDate: nextEnd,
      });
      if (nextLicense !== 'all') params.set('license_id', nextLicense);

      const todayParams = nextLicense === 'all' ? '' : `?license_id=${encodeURIComponent(nextLicense)}`;
      const [summaryData, detailedData, todayData] = await Promise.all([
        api.get<SalesSummary>(`/api/reports/summary?${params}`),
        api.get<{ reports: DetailedReport[] }>(`/api/reports/detailed?${params}`),
        api.get<TodayLiveSummary>(`/api/reports/today${todayParams}`),
      ]);
      const includesToday = todayData.date >= nextStart && todayData.date <= nextEnd;
      const includeLiveSales = includesToday && !todayData.has_closure;
      const totalItems = summaryData.total_items + (includeLiveSales ? todayData.total_items : 0);
      const mergedSummary: SalesSummary = {
        ...summaryData,
        total_sales: summaryData.total_sales + (includeLiveSales ? todayData.total_sales : 0),
        total_items: totalItems,
        average_sale: totalItems > 0
          ? (summaryData.total_sales + (includeLiveSales ? todayData.total_sales : 0)) / totalItems
          : 0,
      };
      const liveReport: DetailedReport | null = includeLiveSales
        ? {
            id: `live-${todayData.date}-${nextLicense}`,
            date: todayData.date,
            total: todayData.total_sales,
            items: todayData.total_items,
            license_id: nextLicense,
            revenue_breakdown: null,
            payment_method: 'VeriFactu (en vivo)',
          }
        : null;
      setSummary(mergedSummary);
      setReports(liveReport ? [liveReport, ...detailedData.reports] : detailedData.reports);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los informes');
      setSummary(null);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const initialFilters = initialFiltersRef.current;
    void loadReports(initialFilters.startDate, initialFilters.endDate, initialFilters.licenseId);
  }, [loadReports]);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;
    const socket = io('/owner', {
      path: '/socket.io/',
      auth: { token },
      transports: ['websocket', 'polling'],
    });
    const onSaleRegistered = (sale: { licenseId?: number; fechaExpedicion?: string }) => {
      const filters = activeFiltersRef.current;
      const saleDate = sale.fechaExpedicion ?? '';
      const belongsToSelectedLicense = filters.licenseId === 'all'
        || String(sale.licenseId ?? '') === filters.licenseId;
      if (belongsToSelectedLicense && saleDate >= filters.startDate && saleDate <= filters.endDate) {
        void loadReports(filters.startDate, filters.endDate, filters.licenseId);
      }
    };
    socket.on('sale:new', onSaleRegistered);
    return () => {
      socket.off('sale:new', onSaleRegistered);
      socket.disconnect();
    };
  }, [loadReports]);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    void loadReports(startDate, endDate, licenseId);
  }

  const businessOptions = [
    { value: 'all', label: 'Todos los locales' },
    ...businesses.map((b) => ({ value: b.license_id, label: b.pos_name })),
  ];

  const businessNameById = Object.fromEntries(
    businesses.map((b) => [b.license_id, b.pos_name])
  );

  return (
    <div>
      <div className="panel-page-header">
        <div>
          <h1 className="page-title">Informes en la nube</h1>
          <p className="page-subtitle">
            Cierres Z y ventas VeriFactu actualizadas en tiempo real desde tus TPV conectados.
          </p>
        </div>
      </div>

      <Card title="Filtros">
        <form onSubmit={handleSubmit} className="filters-row">
          <Input
            label="Desde"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Input
            label="Hasta"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <Select
            label="Local"
            value={licenseId}
            onChange={(e) => setLicenseId(e.target.value)}
            options={businessOptions}
          />
          <Button type="submit" loading={loading}>
            Aplicar
          </Button>
        </form>
      </Card>

      {error ? (
        <div style={{ marginTop: '1rem' }}>
          <Alert tone="warning">{error}</Alert>
        </div>
      ) : null}

      <div className="grid-3" style={{ margin: '1.5rem 0' }}>
        <StatCard
          label="Ventas totales"
          value={summary ? formatCurrency(summary.total_sales) : '—'}
        />
        <StatCard label="Tickets" value={summary ? String(summary.total_items) : '—'} />
        <StatCard
          label="Ticket medio"
          value={summary ? formatCurrency(summary.average_sale) : '—'}
        />
      </div>

      {reports.length > 0 ? <ReportsCharts reports={reports} /> : null}

      <Card title="Detalle de cierres y ventas en curso">
        {reports.length === 0 ? (
          <p className="ui-empty">
            No hay ventas ni cierres en este periodo.
          </p>
        ) : (
          <div className="ui-table-wrap">
            <table className="ui-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Local</th>
                  <th>Ventas</th>
                  <th>Tickets</th>
                  <th>Métodos de pago</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr key={report.id}>
                    <td>{formatDate(report.date)}</td>
                    <td>{report.license_id === 'all' ? 'Todos los locales' : businessNameById[report.license_id] ?? report.license_id}</td>
                    <td>{formatCurrency(Number(report.total))}</td>
                    <td>{report.items ?? '—'}</td>
                    <td>{report.payment_method}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
